import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { baseUrl } from "../index";
import API from "../utils/API";
import OtpPopup from "../components/OtpPopup";
import "./ProposalPage.css";

const policyBaseUrl =
  process.env.NODE_ENV === "development"
    ? "https://preprod.smcinsurance.com"
    : "https://smcinsurance.com";

const isNonEmpty = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
};

const humanize = (key) =>
  String(key || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());

const LABEL_OVERRIDES = {
  passportNumber: "PASSPORT NO",
  PassportNo: "PASSPORT NO",
  ProposerPAN: "PROPOSER PAN",
  panNumber: "PROPOSER PAN",
  PANNumber: "PROPOSER PAN",
  // member fields
  FullName: "Full Name",
  DoB: "Date of Birth",
  NameOfNominee: "Nominee Name",
  RelationWithNomineeId: "Relation With Nominee",
  RelationWithProposerId: "Relation With Proposer",
  OccupationId: "Occupation",
  VisaType: "Visa Type",
};

const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

// Keys inside a member object that should never be shown to the user
const MEMBER_SKIP_KEYS = [
    "MemberKey",
    "RelationWithProposerId",
    "RelationWithNomineeId",
];

// Keys on the MemberDetails wrapper that are not displayable fields
const MEMBER_WRAPPER_SKIP_KEYS = [
  "ListOfMembers",
  "EnquiryNo",
  "InsurerID",
  "PlanId",
  "QuoteTransKey",
    "LotNo",
    "RelationWithProposerId",
    "RelationWithNomineeId",
];

const ADDRESS_SKIP_KEYS = [
    "stateCity",
    "DistrictId",
    "CityId",
    "StateId",
    "districtId",
    "cityId",
    "stateId",
    "DistrictID",
    "CityID",
    "StateID",
];

const stripDisplayId = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";

  if (text.includes("~")) {
    return text.split("~").pop().trim();
  }

  if (/^\d{1,2}[-/]\d{1,2}[-/]\d{2,4}$/.test(text)) {
    return text;
  }

  const prefixed = text.match(/^\s*\d+\s*[-:)\]]\s*(.+)$/);
  if (prefixed?.[1]) return prefixed[1].trim();

  return text;
};

const formatDobDisplay = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";

  const ymd = text.match(/^(\d{4})[-/](\d{2})[-/](\d{2})$/);
  if (ymd) return `${ymd[3]}-${ymd[2]}-${ymd[1]}`;

  const dmy = text.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (dmy) return `${dmy[1]}-${dmy[2]}-${dmy[3]}`;

  return text;
};

const formatValue = (value, key) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value === null || value === undefined || value === "") return "";
  if (Array.isArray(value)) {
    return value
      .map((item) => formatValue(item, key))
      .filter(Boolean)
      .join(", ");
  }
  if (typeof value === "object") {
    if (key === "stateCity") {
      return [
        value?.state_Name || value?.stateName || "",
        value?.cityName || "",
      ]
        .filter(Boolean)
        .join(" / ");
    }
    return stripDisplayId(
      value?.label ||
      value?.value ||
      value?.MasterName ||
      JSON.stringify(value),
    );
  }
  if (
    String(key || "")
      .toLowerCase()
      .includes("dob")
  ) {
    return formatDobDisplay(value);
  }
  return stripDisplayId(value);
};


const maskKeepEdges = (text, start = 0, end = 0) => {
    const s = String(text || "");
    if (!s) return "";
    if (s.length <= start + end) return "*".repeat(s.length);
    return (
        s.slice(0, start) +
        "*".repeat(s.length - start - end) +
        (end ? s.slice(-end) : "")
    );
};

// "Rahul Sharma" -> "R**** S*****"
const maskName = (value) =>
    String(value || "")
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => maskKeepEdges(w, 1, 0))
        .join(" ");

// 9876543210 -> ******3210
const maskMobile = (value) => {
    const digits = String(value || "").replace(/\s+/g, "");
    return maskKeepEdges(digits, 0, 4);
};

// ABCDE1234F -> AB*******F
const maskPan = (value) => maskKeepEdges(String(value || "").trim(), 2, 1);

// A1234567 -> A*****67
const maskPassport = (value) => maskKeepEdges(String(value || "").trim(), 1, 2);

// 122001 -> 12****
const maskPincode = (value) => maskKeepEdges(String(value || "").trim(), 2, 0);

// rahul.sharma@gmail.com -> ra*********@gmail.com
const maskEmail = (value) => {
    const text = String(value || "").trim();
    const at = text.indexOf("@");
    if (at <= 0) return maskKeepEdges(text, 2, 0);
    const local = text.slice(0, at);
    const domain = text.slice(at);
    return maskKeepEdges(local, 2, 0) + domain;
};

// House 12, Sector 45 -> Hou*************
const maskAddress = (value) => maskKeepEdges(String(value || "").trim(), 3, 0);

// 25-08-1990 / 1990-08-25 -> 25-08-****
const maskDobYear = (value) => {
    const text = String(value || "").trim();
    const dmy = text.match(/^(\d{2})([-/])(\d{2})([-/])(\d{4})$/);
    if (dmy) return `${dmy[1]}${dmy[2]}${dmy[3]}${dmy[4]}****`;
    const ymd = text.match(/^(\d{4})([-/])(\d{2})([-/])(\d{2})$/);
    if (ymd) return `${ymd[5]}${ymd[4]}${ymd[3]}${ymd[2]}****`;
    return text.replace(/\d{4}/, "****");
};

// key ke hisaab se sahi mask choose karta hai
const maskByKey = (key, value) => {
    if (!value) return value;
    const k = String(key || "");

    if (/dob/i.test(k)) return maskDobYear(value);
    if (/email/i.test(k)) return maskEmail(value);
    if (/mobile|phone/i.test(k)) return maskMobile(value);
    if (/pan(no|number)?$/i.test(k)) return maskPan(value);
    if (/passport/i.test(k)) return maskPassport(value);
    if (/pin\s?code|^pin$|zip/i.test(k)) return maskPincode(value);
    if (/address/i.test(k)) return maskAddress(value);
    if (/^(full|first|middle|last)?name$|nameofnominee|nomineename/i.test(k))
        return maskName(value);

    return value;
};

/*const filterEntries = (data, skipKeys = []) =>
  Object.entries(data || {})
    .filter(([key, value]) => !skipKeys.includes(key) && isNonEmpty(value))
    .map(([key, value]) => ({
      label: LABEL_OVERRIDES[key] || humanize(key),
      value: formatValue(value, key),
    }));*/


const filterEntries = (data, skipKeys = []) =>
    Object.entries(data || {})
        .filter(([key, value]) => !skipKeys.includes(key) && isNonEmpty(value))
        .map(([key, value]) => ({
            label: LABEL_OVERRIDES[key] || humanize(key),
            value: maskByKey(key, formatValue(value, key)),
        }));

// Builds display rows for a single member object from ListOfMembers
/*const buildMemberEntries = (member) =>
  Object.entries(member || {})
    .filter(([key, value]) => {
      if (MEMBER_SKIP_KEYS.includes(key)) return false;
      if (!isNonEmpty(value)) return false;
      // hide unset ids such as OccupationId: 0
      if (/Id$/.test(key) && Number(value) === 0) return false;
      return true;
    })
    .map(([key, value]) => ({
      label: LABEL_OVERRIDES[key] || humanize(key),
      value: formatValue(value, key),
    }));*/

const buildMemberEntries = (member) =>
    Object.entries(member || {})
        .filter(([key, value]) => {
            if (MEMBER_SKIP_KEYS.includes(key)) return false;
            if (!isNonEmpty(value)) return false;
            if (/Id$/.test(key) && Number(value) === 0) return false;
            return true;
        })
        .map(([key, value]) => ({
            label: LABEL_OVERRIDES[key] || humanize(key),
            value: maskByKey(key, formatValue(value, key)),
        }));

const SectionCard = ({ title, subtitle, entries, onEdit }) => {
  if (!entries?.length) return null;

  return (
    <section className="pg-summary-section">
      <div className="pg-summary-section-head">
        <div>
          <div className="pg-summary-section-kicker">{title}</div>
          {subtitle ? (
            <div className="pg-summary-section-sub">{subtitle}</div>
          ) : null}
        </div>
        {onEdit ? (
          <button
            type="button"
            className="pg-summary-edit-btn"
            onClick={onEdit}
          >
            <svg
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 16 16"
              width="12"
              height="12"
              aria-hidden="true"
            >
              <path
                d="M11.5 1.5l3 3-8.5 8.5H3v-3L11.5 1.5z"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10 3l3 3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Edit
          </button>
        ) : null}
      </div>
      <div className="pg-summary-field-grid">
        {entries.map((item) => (
          <div className="pg-summary-field" key={`${title}-${item.label}`}>
            <span className="pg-summary-field-label">{item.label}</span>
            <p className="pg-summary-field-value">{item.value}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const PaymentSummery = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const quoteDetails = useSelector((state) => state.QuoteDetails);
  const travellerDetails = useSelector((state) => state.TravellerDetails);
  const lastSummaryFetchKeyRef = useRef("");

  // State (declared before any hook/memo that reads it)
  const [proposalSummaryResponse, setProposalSummaryResponse] = useState(null);
  const [paymentHtml] = useState("");
  const [otherObj, setOtherObj] = useState({
    askOtp: false,
    OTP: "",
    PaymentUrl: "",
    ReferenceNo: "",
  });
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);

  const locationState = location?.state || EMPTY_OBJECT;
  const quoteData =
    locationState?.quoteData ||
    travellerDetails?.quoteData ||
    quoteDetails?.selectedQuotes ||
    quoteDetails?.quotes?.[0] ||
    {};
  const finalPremium = quoteData?.NetPremium ?? quoteData?.finalPremium ?? 0;
  const proposalResponse =
    locationState?.proposalResponse || travellerDetails?.proposalResponse || {};
  const proposalData =
    locationState?.ProposalData || travellerDetails?.proposalData || {};
  const travellers =
    locationState?.travellers || travellerDetails?.travellers || EMPTY_ARRAY;
  const locationData = {
    destination:
      locationState?.locationData?.destination ||
      proposalData?.destination ||
      proposalData?.DestinationList ||
      travellerDetails?.destination ||
      [],
    startDate:
      locationState?.locationData?.startDate ||
      proposalData?.startDate ||
      proposalData?.StartDate ||
      travellerDetails?.startDate ||
      travellerDetails?.StartDate ||
      "",
    endDate:
      locationState?.locationData?.endDate ||
      proposalData?.endDate ||
      proposalData?.EndDate ||
      travellerDetails?.endDate ||
      travellerDetails?.EndDate ||
      "",
    numberofDays:
      locationState?.locationData?.numberofDays ||
      proposalData?.numberofDays ||
      proposalData?.NumberOfDays ||
      travellerDetails?.numberofDays ||
      travellerDetails?.NumberOfDays ||
      0,
    Travellers:
      locationState?.locationData?.Travellers ||
      proposalData?.Travellers ||
      travellerDetails?.travellers ||
      [],
  };

  const proposer = useMemo(
    () => travellers?.[0] || travellerDetails?.travellers?.[0] || {},
    [travellers, travellerDetails],
  );
  const memberDetails = useMemo(
    () =>
      travellerDetails?.memberDetails ||
      travellerDetails?.member ||
      locationState?.memberDetails ||
      {},
    [travellerDetails, locationState],
  );
  const medicalQuestions = useMemo(
    () =>
      travellerDetails?.memberQuestions ||
      travellerDetails?.questions ||
      travellerDetails?.medicalQuestions ||
      locationState?.memberQuestions ||
      {},
    [travellerDetails, locationState],
  );
  const contactDetails = useMemo(
    () =>
      travellerDetails?.contactDetails ||
      travellerDetails?.contactAddress ||
      travellerDetails?.addressDetails ||
      locationState?.contactDetails ||
      {},
    [travellerDetails, locationState],
  );

  const proposalSummaryPayload = useMemo(() => {
    const sourceQuote =
      locationState?.quoteData ||
      quoteData ||
      quoteDetails?.selectedQuotes ||
      quoteDetails?.quotes?.[0] ||
      {};

    const enquiryNo =
      sourceQuote?.enquiryNo ||
      sourceQuote?.EnquiryNo ||
      travellerDetails?.enquiryNo ||
      travellerDetails?.EnquiryNo ||
      "";
    const lotNo =
      sourceQuote?.lotNo ||
      sourceQuote?.LotNo ||
      travellerDetails?.lotNo ||
      travellerDetails?.LotNo ||
      "";
    const planId = Number(
      sourceQuote?.PlanId ||
      sourceQuote?.plansID ||
      sourceQuote?.planId ||
      sourceQuote?.PlansID ||
      0,
    );
    const insurerId = Number(
      sourceQuote?.InsurerID ||
      sourceQuote?.InsurerId ||
      sourceQuote?.insurerID ||
      sourceQuote?.insurerId ||
      0,
    );

    if (!enquiryNo || !lotNo || !planId || !insurerId) return null;

    return {
      EnquiryNo: enquiryNo,
      LotNo: lotNo,
      PlanId: planId,
      InsurerId: insurerId,
    };
  }, [quoteData, travellerDetails]);

  useEffect(() => {
    const fetchProposalSummary = async () => {
      if (!proposalSummaryPayload) return;

      const fetchKey = JSON.stringify(proposalSummaryPayload);
      if (lastSummaryFetchKeyRef.current === fetchKey) return;
      lastSummaryFetchKeyRef.current = fetchKey;

      try {
        const response = await API.CallRequestWithHeader({
          URL: "GetProposalSummary",
          PostString: JSON.stringify(proposalSummaryPayload),
        });
        setProposalSummaryResponse(response || null);

        if (response?.StatusCode !== 200 && response?.statusCode !== 200) {
          console.warn(
            "GetProposalSummary returned a non-success response",
            response,
          );
        }
      } catch (error) {
        console.error("Failed to load proposal summary", error);
      }
    };

    fetchProposalSummary();
  }, [proposalSummaryPayload]);

  // Members coming from the GetProposalSummary API (MemberDetails.ListOfMembers)
  const summaryMembers = useMemo(() => {
    const root =
      proposalSummaryResponse?.Response ||
      proposalSummaryResponse?.response ||
      proposalSummaryResponse ||
      {};
    const md =
      root?.MemberDetails || root?.memberDetails || memberDetails || {};
    if (Array.isArray(md)) return md;
    return Array.isArray(md?.ListOfMembers) ? md.ListOfMembers : [];
  }, [proposalSummaryResponse, memberDetails]);

  const sections = useMemo(() => {
    const next = [];

    const proposerEntries = filterEntries(proposer, [
      "Relation",
      "stateCity",
      "contactDetails",
      "contactAddress",
      "addressDetails",
      "memberDetails",
      "memberQuestions",
      "questions",
      "medicalQuestions",
      "proposalResponse",
      "proposalReq",
      "proposalData",
    ]);
    if (proposerEntries.length) {
      next.push({
        stepKey: "1",
        title: "Proposer Details",
        subtitle: "Traveller 1 information",
        entries: proposerEntries,
      });
    }

    if (summaryMembers.length) {
      // One card per member
      summaryMembers.forEach((member, index) => {
        const entries = buildMemberEntries(member);
        if (!entries.length) return;
        const fullName = member?.FullName
          ? `${member?.Salutation ? `${member.Salutation} ` : ""}${member.FullName}`
          : "";
        next.push({
          stepKey: "2",
          title: `Member ${index + 1} Details`,
          subtitle: fullName || "Insured member information",
          entries,
        });
      });
    } else {
      // Fallback for non-array shapes
      const memberEntries = filterEntries(
        memberDetails,
        MEMBER_WRAPPER_SKIP_KEYS,
      );
      if (memberEntries.length) {
        next.push({
          stepKey: "2",
          title: "Member Details",
          subtitle:
            travellers.length > 1
              ? "Insured member information"
              : "Insured traveller information",
          entries: memberEntries,
        });
      }
    }

    const questionEntries = filterEntries(medicalQuestions);
    if (questionEntries.length) {
      next.push({
        stepKey: "3",
        title: "Additional Details",
        subtitle: "Medical and travel declarations",
        entries: questionEntries,
      });
    }

      //const contactEntries = filterEntries(contactDetails, ["stateCity"]);
      const contactEntries = filterEntries(contactDetails, ADDRESS_SKIP_KEYS);
    if (contactEntries.length) {
      next.push({
        stepKey: "4",
        title: "Communication Address",
        subtitle: "Correspondence and contact information",
        entries: contactEntries,
      });
    }

    return next;
  }, [
    contactDetails,
    medicalQuestions,
    memberDetails,
    summaryMembers,
    proposer,
    travellers.length,
  ]);

  const handleEditSection = (section) => {
    if (!section?.stepKey) return;
    navigate("/Proposal", {
      state: {
        ...locationState,
        QuoteData: quoteData,
        locationData,
        proposalResponse,
        ProposalData: proposalData,
        travellers,
        editStep: section.stepKey,
      },
    });
  };

  const formatINR = (value) =>
    new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 0,
    }).format(Number(value || 0));

  const formatCoverageDate = (value) => {
    if (!value) return "—";
    const raw = String(value).trim();
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    }

    const match = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (match) {
      const iso = `${match[3]}-${match[2]}-${match[1]}`;
      const fallback = new Date(iso);
      if (!Number.isNaN(fallback.getTime())) {
        return fallback.toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
      }
    }

    return raw;
  };

  const destinations =
    locationData?.destination
      ?.map?.((d) =>
        typeof d === "string" ? d : d?.CountryName || d?.label || "",
      )
      .filter(Boolean) || [];
  const travellerChips =
    (locationData?.Travellers || travellers)?.map?.((t, i) => ({
      label: i === 0 ? `Self · ${t.age || ""}Y` : `Member · ${t.age || ""}Y`,
    })) || [];

  const handleGoBack = () => {
    navigate("/Quotation", { replace: true });
  };

  const getProposalTransKey = () =>
    proposalResponse?.response?.trankey ||
    proposalResponse?.response?.tranKey ||
    proposalResponse?.response?.transKey ||
    proposalResponse?.response?.TransKey ||
    proposalResponse?.trankey ||
    proposalResponse?.tranKey ||
    proposalResponse?.transKey ||
    proposalResponse?.TransKey ||
    "";

  const getInsurerId = () =>
    quoteData?.InsurerID ||
    quoteData?.InsurerId ||
    quoteData?.insurerID ||
    quoteData?.insurerId ||
    "";

  const sendOtp = async () => {
    if (isSendingOtp || !agreeTerms) return;
    try {
      setIsSendingOtp(true);
      const transKey = getProposalTransKey();

      if (!transKey) {
        toast.error("Unable to fetch proposal transkey");
        return;
      }

      // InsurerID is now taken from the selected quote (e.g. Bajaj)
      const payload = {
        EnquiryNo: quoteData?.enquiryNo || travellerDetails?.enquiryNo || "",
        InsurerID: getInsurerId(),
        PropTransKey: transKey,
      };

      const response = await API.CallRequestWithHeader({
        URL: "SendOTP",
        PostString: JSON.stringify(payload),
      });

      if (response?.StatusCode === 200 || response?.statusCode === 200) {
        const referenceNo = response?.Response?.ReferenceNo || "";

        setOtherObj((prev) => ({
          ...prev,
          askOtp: true,
          OTP: "",
          ReferenceNo: referenceNo,
        }));
        return;
      }

      toast.error("Unable to send OTP");
    } catch (error) {
      toast.error("Unable to send OTP");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    if (!otherObj?.OTP) {
      toast.error("Please enter OTP");
      return;
    }

    try {
      setIsVerifyingOtp(true);
      const transKey = getProposalTransKey();

      if (!transKey) {
        toast.error("Unable to fetch proposal transkey");
        return;
      }

      const payload = {
        EnquiryNo: quoteData?.enquiryNo || travellerDetails?.enquiryNo || "",
        InsurerID: getInsurerId(),
        PropTransKey: transKey,
        ReferenceNo: otherObj?.ReferenceNo || "",
        OTP: otherObj?.OTP,
      };

      const res = await API.CallRequestWithHeader({
        URL: "VerifyOTP",
        PostString: JSON.stringify(payload),
      });

      if (res?.StatusCode === 200 || res?.statusCode === 200) {
        const paymentUrl = res?.response || res?.Response || "";

        if (!paymentUrl) {
          toast.error("Payment URL not received");
          return;
        }

        // close the modal and clear OTP state in one update
        setOtherObj((prev) => ({
          ...prev,
          askOtp: false,
          OTP: "",
          PaymentUrl: paymentUrl,
        }));

        // let React render the closed modal before leaving the page
        setTimeout(() => {
          window.location.href = paymentUrl;
        }, 0);
      } else {
        toast.error(res?.Message || res?.message || "OTP verification failed");
      }
    } catch (err) {
      toast.error("OTP verification failed");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  return (
    <div className="pg-page">
      {!paymentHtml && (
        <>
          <div className="pg-banner">
            <div className="container">
              <div className="pg-banner-inner">
                <div className="pg-banner-left">
                  <button
                    type="button"
                    className="pg-back-link"
                    onClick={handleGoBack}
                  >
                    <i className="fa fa-arrow-left" /> Back to Quotations
                  </button>
                  <div>
                    <div className="pg-banner-plan-name">Proposal Summary</div>
                    <div className="pg-banner-plan-sub">
                      Review your saved proposal details
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="container">
            <div className="pg-summary-layout">
              <div className="pg-summary-main">
                <div className="pg-summary-intro">
                  <div className="pg-summary-intro-kicker">Proposal Ready</div>
                  <h1>Review every section before you proceed to payment.</h1>
                  <p>
                    We've gathered the saved data from your proposal flow into
                    one clean, premium summary.
                  </p>
                </div>
                <div className="pg-summary-sections">
                  {sections.map((section) => (
                    <SectionCard
                      key={section.title}
                      title={section.title}
                      subtitle={section.subtitle}
                      entries={section.entries}
                      onEdit={() => handleEditSection(section)}
                    />
                  ))}
                </div>
                <div className="pg-pay-panel">
                  <div className="pg-pay-panel-left">
                    <div className="pg-pay-label">Ready to proceed</div>
                    <div className="pg-pay-title">
                      Secure your policy with a quick OTP confirmation.
                    </div>
                    <label className="pg-pay-consent">
                      <input
                        type="checkbox"
                        checked={agreeTerms}
                        onChange={(e) => setAgreeTerms(e.target.checked)}
                        aria-label="I agree to all terms and conditions"
                      />
                      <span className="pg-pay-consent-text">
                        By clicking <strong>Ready to Proceed</strong>, I agree
                        to all{" "}
                        <a
                          href={`${policyBaseUrl}/corporate-governance-and-legal-policies`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          T &amp; C
                        </a>
                      </span>
                    </label>
                  </div>
                  <button
                    className="pg-pay-btn"
                    onClick={sendOtp}
                    type="button"
                    disabled={isSendingOtp || !agreeTerms}
                    aria-busy={isSendingOtp}
                  >
                    <span className="pg-pay-btn-main">
                      {isSendingOtp ? "Sending OTP..." : "Pay Now"}
                    </span>
                    {isSendingOtp ? (
                      <span className="pg-pay-btn-loader" aria-hidden="true" />
                    ) : null}
                    <span className="pg-pay-btn-amt">
                      ₹ {formatINR(finalPremium)}
                    </span>
                    <span className="pg-pay-btn-icon">
                      <i className="fa fa-angle-right" />
                    </span>
                  </button>
                </div>
              </div>

              <aside className="pg-summary-side">
                <div className="pg-sidebar">
                  <div className="pg-summary-card">
                    <div className="pg-summary-header">
                      <span className="pg-summary-header-title">
                        Policy Summary
                      </span>
                    </div>
                    <div className="pg-summary-body">
                      <div className="pg-summary-row">
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          <div
                            className="pg-banner-logo"
                            style={{ width: 44, height: 44 }}
                          >
                            {quoteData?.InsurerLogo ? (
                              <img
                                src={quoteData.InsurerLogo}
                                alt={quoteData.planName}
                              />
                            ) : (
                              <svg
                                fill="none"
                                stroke="var(--brand-primary-deep)"
                                strokeWidth="1.5"
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                              >
                                <path d="M12 3L4 7v5c0 5 4.5 9.5 8 11 3.5-1.5 8-6 8-11V7L12 3z" />
                              </svg>
                            )}
                          </div>
                          <div>
                            <div className="pg-summary-row-val">
                              {quoteData?.planName ||
                                quoteData?.PlanName ||
                                "Travel Insurance Plan"}
                            </div>
                            <div
                              style={{
                                fontSize: 11.5,
                                color: "var(--pg-text-3)",
                                marginTop: 2,
                              }}
                            >
                              {quoteData?.ProductName ||
                                quoteData?.productName ||
                                "Policy snapshot"}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="pg-summary-row">
                        <div className="pg-summary-row-label">Enquiry No</div>
                        <div className="pg-summary-row-val">
                          {quoteData?.enquiryNo ||
                            travellerDetails?.enquiryNo ||
                            "—"}
                        </div>
                      </div>

                      <div className="pg-summary-row">
                        <div className="pg-summary-row-label">
                          Final Premium
                        </div>
                        <div className="pg-summary-row-val large">
                          ₹
                          {formatINR(
                            quoteData?.NetPremium
                              ? quoteData.NetPremium
                              : quoteData?.finalPremium || 0,
                          )}
                        </div>
                      </div>

                      <div className="pg-summary-divider" />

                      <div className="pg-summary-row">
                        <div className="pg-summary-row-label">Policy Term</div>
                        <div className="pg-summary-row-val">
                          {locationData?.numberofDays
                            ? `${locationData.numberofDays} Days`
                            : "1 Year"}
                        </div>
                      </div>

                      <div className="pg-summary-row">
                        <div className="pg-summary-row-label">
                          Coverage Period
                        </div>
                        <div className="pg-summary-date-row">
                          <div className="pg-summary-date-block">
                            <div className="pg-summary-date-label">From</div>
                            <div className="pg-summary-date-val">
                              {locationData?.startDate
                                ? formatCoverageDate(locationData.startDate)
                                : "—"}
                            </div>
                          </div>
                          <div className="pg-summary-date-block">
                            <div className="pg-summary-date-label">To</div>
                            <div className="pg-summary-date-val">
                              {locationData?.endDate
                                ? formatCoverageDate(locationData.endDate)
                                : "—"}
                            </div>
                          </div>
                        </div>
                      </div>

                      {travellerChips.length > 0 && (
                        <>
                          <div className="pg-summary-divider" />
                          <div className="pg-summary-row">
                            <div className="pg-summary-row-label">Members</div>
                            <div className="pg-member-chips">
                              {travellerChips.map((c, i) => (
                                <span key={i} className="pg-member-chip">
                                  <svg
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 16 16"
                                    width="11"
                                    height="11"
                                  >
                                    <circle cx="8" cy="5" r="2.5" />
                                    <path
                                      d="M3 14c0-2.8 2.2-5 5-5s5 2.2 5 5"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                  {c.label}
                                </span>
                              ))}
                            </div>
                          </div>
                        </>
                      )}

                      {destinations.length > 0 && (
                        <>
                          <div className="pg-summary-divider" />
                          <div className="pg-summary-row">
                            <div className="pg-summary-row-label">
                              Destination
                            </div>
                            <div
                              className="pg-summary-row-val"
                              style={{ fontSize: 12 }}
                            >
                              {destinations.join(", ")}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </>
      )}

      {paymentHtml && <div dangerouslySetInnerHTML={{ __html: paymentHtml }} />}

      {otherObj.askOtp && (
        <Modal show centered className="customeName">
          <OtpPopup
            mobile={
              proposalResponse?.response?.mobileNo ||
              proposalResponse?.response?.MobileNo ||
              proposalResponse?.response?.mobile ||
              proposalResponse?.response?.Mobile ||
              proposalResponse?.mobileNo ||
              proposalResponse?.MobileNo ||
              ""
            }
            isSubmitting={isVerifyingOtp}
            referenceNo={otherObj?.ReferenceNo}
            handle={(e) =>
              setOtherObj((prev) => ({ ...prev, OTP: e.target.value }))
            }
            submit={verifyOtp}
            close={() => setOtherObj((prev) => ({ ...prev, askOtp: false }))}
          />
        </Modal>
      )}
    </div>
  );
};

export default PaymentSummery;